import React from 'react';
import { Link, useLocation, LinkProps } from 'react-router-dom';
import { styledComponent } from '@dewise-devex/backstage-plugin-mui-uikit-react';

/**
 * Represents a navigation item in the top navigation bar.
 *
 * @category Navigation
 */
export type RouteItem = {
  /** The display text for the navigation link. */
  title: string;

  /** An icon component representing the navigation item. */
  icon: React.ElementType;

  /** The URL path the navigation link points to. */
  url: string;
};

/**
 * Represents the properties for the navigation item component.
 *
 * @category Navigation
 */
interface NavigationItemProps extends LinkProps {
  /** Indicates whether the navigation item is currently active (selected). */
  active: boolean;
}

/**
 * Styled link component representing an individual navigation item.
 *
 * @param theme - The theme object used for styling.
 * @param active - Boolean indicating whether the link is active.
 *
 * @category Navigation
 */
const LinkItem = styledComponent<NavigationItemProps>(Link, {
  ignoreProps: ['active'],
})(({ theme, active }) => ({
  display: 'flex',
  alignItems: 'center',
  color: active
    ? theme.palette.primary.main
    : theme.palette.surface.onSurfaceVar,
  textDecoration: 'none',
  marginLeft: theme.spacing(2),
  fontSize: '16px',
  fontWeight: 700,
  transition: 'color 0.3s ease, background-color 0.3s ease',
  '& span': {
    padding: '6px',
    lineHeight: '9px',
    position: 'relative',
    borderRadius: '50%',
    transition: 'background-color 0.3s ease',
    backgroundColor: active ? theme.palette.primary.main : 'transparent',
  },
  '& span svg': {
    fill: active
      ? theme.palette.surfaceContainer.lowest
      : theme.palette.surface.onSurfaceVar,
    transition: 'fill 0.3s ease',
  },
  '&:active span': {
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(29, 27, 32, 0.12)',
      borderRadius: '50%',
      zIndex: 1,
      transition: 'background-color 0.3s ease',
    },
  },
  '&:active span svg': {
    position: 'relative',
    zIndex: 2,
  },
  '&:hover span': {
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(29, 27, 32, 0.08)',
      borderRadius: '50%',
      zIndex: 1,
      transition: 'background-color 0.3s ease',
    },
  },
  '&:hover span svg': {
    position: 'relative',
    zIndex: 2,
  },
}));

/**
 * Styled component for the text next to the icon in the navigation item.
 *
 * @category Navigation
 */
const LinkText = styledComponent('div')(() => ({
  paddingLeft: '4px',
  paddingTop: '5px',
}));

/**
 * Renders a single navigation item as part of the top navigation bar.
 * It displays an icon, title, and handles active state styling.
 *
 * @param props - The properties for the navigation item.
 * @returns A React element representing the navigation item.
 *
 * @category Navigation
 */
const NavigationItem = (props: RouteItem): React.ReactElement => {
  const location = useLocation();
  const isActive = location.pathname === props.url;

  return (
    <LinkItem to={props.url} active={isActive} key={props.url}>
      <span>
        <props.icon />
      </span>
      <LinkText>{props.title}</LinkText>
    </LinkItem>
  );
};

export default NavigationItem;
